import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@/public/icons/approval/refresh.svg';
import useApprovalAiSummary from '@/hooks/approval/useApprovalAiSummary';
import {
  AiSummaryBody,
  AiSummaryBox,
  AiSummaryChip,
  AiSummaryChipButton,
  AiSummaryHeader,
  AiSummaryLine,
  AiSummaryMessage,
  AiSummarySpinner,
} from './styles';
import type { ApprovalViewAi, DocumentStatus } from './types';

/**
 * AI 요약 영역 — v2 단건조회 ai 로 초기 상태 산정, 진행 중이면 SSE 구독 후 GET 재조회.
 * 재시도는 chip 안 refresh 아이콘(POST 후 SSE 대기), loading 중엔 spinner 로 교체.
 */

/**
 * 요약 텍스트를 줄 단위로 쪼갠다.
 *
 * 항목 사이 간격은 CSS(AiSummaryLine)가 주므로, 간격용으로 들어온 빈 줄은 걸러
 * 항목 사이가 두 배로 벌어지지 않게 한다. 줄 앞 들여쓰기는 그대로 넘겨 pre-wrap 이 살린다.
 */
const toSummaryLines = (summary: string) =>
  summary.split(/\r?\n/).filter((line) => line.trim().length > 0);

type AiSummarySlotProps = {
  documentId: string;
  ai?: ApprovalViewAi | null; // v2 단건조회 응답의 ai 객체.
  content?: string | null; // 본문 텍스트. 비어있고 서브 컨텐츠도 없으면 즉시 no-content. forceRefresh/임시저장 케이스에서는 무시.
  subContent?: unknown[] | unknown | null; // 1번 유형은 null. 다른 유형 추가 시 attributes 매핑.
  documentStatus?: DocumentStatus | null; //  문서 상태. 'S' (임시저장) 면 mount 시 자동 retry 1회
};

const AiSummarySlot = ({
  documentId,
  ai,
  content,
  subContent,
  documentStatus,
}: AiSummarySlotProps) => {
  const { t } = useTranslation();

  const { status, summary, canRetry, retry } = useApprovalAiSummary({
    documentId,
    ai,
    content,
    subContent,
    documentStatus,
  });

  // chip 우측 아이콘 — success 는 재시도 불필요라 refresh 숨김.
  let chipRightSlot: ReactNode = null;
  if (status === 'loading') {
    chipRightSlot = (
      <AiSummarySpinner aria-label={t('AI 요약을 생성하고 있습니다…')} />
    );
  } else if (status !== 'success') {
    chipRightSlot = (
      <AiSummaryChipButton
        type="button"
        onClick={retry}
        disabled={!canRetry}
        aria-label={t('AI 요약 재시도')}
      >
        <RefreshIcon width={12} height={12} />
      </AiSummaryChipButton>
    );
  }

  // 안내 메시지 (chip 옆 inline 노출). success 일 때는 메시지 대신 본문 영역에 요약을 표시.
  let inlineMessage: string | null = null;
  switch (status) {
    case 'loading':
      inlineMessage = t('AI 요약을 생성하고 있습니다…');
      break;
    case 'no-content':
      inlineMessage = t(
        "품의 '내용'이 비어있어 요약 결과를 제공할 수 없습니다.",
      );
      break;
    case 'error':
      inlineMessage = t('요약 중 에러가 발생했습니다.');
      break;
    case 'idle':
      inlineMessage = t(
        '요약이 준비 중이에요. 잠시 후 다시 버튼을 눌러 확인해 보세요.',
      );
      break;
    case 'success':
    default:
      inlineMessage = null;
      break;
  }

  return (
    <AiSummaryBox>
      <AiSummaryHeader>
        <AiSummaryChip>
          {t('AI 요약')}
          {chipRightSlot}
        </AiSummaryChip>
        {inlineMessage && <AiSummaryMessage>{inlineMessage}</AiSummaryMessage>}
      </AiSummaryHeader>
      {status === 'success' && summary && (
        <AiSummaryBody>
          {toSummaryLines(summary).map((line, index) => (
            // 요약 텍스트는 렌더마다 통째로 교체되므로 index key 로 충분.
            // eslint-disable-next-line react/no-array-index-key
            <AiSummaryLine key={index}>{line}</AiSummaryLine>
          ))}
        </AiSummaryBody>
      )}
    </AiSummaryBox>
  );
};

export default AiSummarySlot;
